import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, Download } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { PeriodPicker, REPORT_UNITS, usePeriod } from '@/components/ui/PeriodPicker';
import { Table } from '@/components/ui/Table';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/form/Select';
import { SearchableSelect, type SearchableOption } from '@/components/form/SearchableSelect';
import { formatINR, formatDate } from '@/lib/format';
import { downloadExcel } from '@/lib/downloadExcel';
import { listSisStores } from '@/api/sisStock';
import { listSisCreditNotes, listSisFifoByItem, sisCreditNotesExportPath, sisFifoByItemExportPath, type SisFifoItemRow } from '@/api/sisCreditNote';
import { GenerateCreditNoteModal } from './GenerateCreditNoteModal';

type PivotRow = {
  key: string;
  storeName: string;
  group: string;
  periodStart: string;
  periodEnd: string;
  createdBy: string | null;
  byCat: Record<string, { noteNo: string; amount: number }>;
  total: number;
};

export function SisCreditNotePage() {
  const [partyId, setPartyId] = useState('');
  const [group, setGroup] = useState('');
  const [type, setType] = useState('');
  // Sale date — All / Date (from–to) / Month / Quarter / Year.
  const period = usePeriod();
  const { range } = period;
  const [genOpen, setGenOpen] = useState(false);

  // Item-wise FIFO cost-vs-sell difference (uses store/group/period filters).
  const fifoFilter = useMemo(
    () => ({ partyId: partyId ? Number(partyId) : undefined, group: group || undefined, from: range?.from, to: range?.to }),
    [partyId, group, range],
  );
  const fifoQ = useQuery({ queryKey: ['sis-fifo-by-item', fifoFilter], queryFn: () => listSisFifoByItem(fifoFilter) });
  const fifoRows = fifoQ.data?.rows ?? [];
  const fifoTotals = fifoQ.data?.totals;

  const filter = useMemo(
    () => ({ partyId: partyId ? Number(partyId) : undefined, type: type || undefined }),
    [partyId, type],
  );
  const storesQ = useQuery({ queryKey: ['sis-stores'], queryFn: () => listSisStores(), staleTime: 0 });
  const query = useQuery({
    queryKey: ['sis-credit-notes', filter],
    queryFn: () => listSisCreditNotes(filter),
    refetchInterval: 60_000,
  });
  const rows = query.data ?? [];

  const groupOptions = useMemo(
    () => Array.from(new Set((storesQ.data ?? []).map((s) => s.group).filter(Boolean))).sort() as string[],
    [storesQ.data],
  );

  // Totals per note category (Commission / Difference / …) for the header.
  const catTotals = useMemo(() => {
    const m: Record<string, number> = {};
    for (const r of rows) { const c = r.category || 'Other'; m[c] = (m[c] ?? 0) + r.amount; }
    return m;
  }, [rows]);

  const storeOptions = useMemo<SearchableOption[]>(
    () => [
      { value: '', label: 'All Stores' },
      ...(storesQ.data ?? []).map((s) => ({ value: String(s.id), label: s.name, sublabel: s.city || undefined })),
    ],
    [storesQ.data],
  );

  // Pivot the notes: one row per store × period, a dynamic column per category
  // (Commission / Difference / …) instead of a separate row per note.
  const CAT_ORDER = ['Commission', 'Difference'];
  const noteCategories = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) set.add(r.category || 'Other');
    return [...set].sort((a, b) => {
      const ia = CAT_ORDER.indexOf(a), ib = CAT_ORDER.indexOf(b);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a.localeCompare(b);
    });
  }, [rows]);
  const pivotRows = useMemo<PivotRow[]>(() => {
    const m = new Map<string, PivotRow>();
    for (const r of rows) {
      const k = `${r.partyId}|${r.periodStart}|${r.periodEnd}`;
      const g = m.get(k) ?? { key: k, storeName: r.storeName, group: r.group, periodStart: r.periodStart, periodEnd: r.periodEnd, createdBy: r.createdBy, byCat: {}, total: 0 };
      g.byCat[r.category || 'Other'] = { noteNo: r.noteNo, amount: r.amount };
      g.total += r.amount;
      m.set(k, g);
    }
    return [...m.values()];
  }, [rows]);

  return (
    <div>
      <SectionHeader
        title="SIS Credit / Debit Notes"      />

      <FilterBar>
        <FilterField label="Group" className="sm:min-w-[150px]">
          <Select value={group} onChange={(e) => { setGroup(e.target.value); setPartyId(''); }}>
            <option value="">All Groups</option>
            {groupOptions.map((g) => <option key={g}>{g}</option>)}
          </Select>
        </FilterField>
        <FilterField label="Store" className="col-span-2 sm:col-auto sm:min-w-[220px]">
          <SearchableSelect
            options={storeOptions.filter((o) => o.value === '' || !group || (storesQ.data ?? []).find((s) => String(s.id) === o.value)?.group === group)}
            value={partyId} onChange={(v) => setPartyId(v === '' ? '' : String(v))} placeholder="Type a store name…" />
        </FilterField>
        <FilterField label="Period" className="col-span-3 sm:col-auto">
          <PeriodPicker {...period.picker} units={REPORT_UNITS} className="justify-start" />
        </FilterField>
        <FilterField label="Type">
          <Select value={type} onChange={(e) => setType(e.target.value)}>
            <option value="">All</option>
            <option>Credit</option>
            <option>Debit</option>
          </Select>
        </FilterField>
        <div className="col-span-3 sm:col-span-1 sm:ml-auto sm:self-end">
          <Button variant="primary" size="sm" icon={<Plus size={12} />} onClick={() => setGenOpen(true)} className="w-full justify-center sm:w-auto">
            Generate CN / DN
          </Button>
        </div>
      </FilterBar>

      {/* Register of issued CN/DN documents — one row per store × period, a
          column per note category (Commission / Difference / …). */}
      {rows.length > 0 && (
        <Table<PivotRow>
          storageKey="sis-credit-notes"
          paginated
          title="Notes"
          count={`${pivotRows.length} store${pivotRows.length === 1 ? '' : 's'} · ${rows.length} notes${noteCategories.map((c) => ` · ${c} ₹${formatINR(catTotals[c] ?? 0)}`).join('')}`}
          titleAction={
            <Button
              variant="glass"
              size="sm"
              icon={<Download size={12} />}
              onClick={() => downloadExcel(sisCreditNotesExportPath(filter), 'GRHAMOY_SIS_CreditNotes.xlsx', 120000)}
              title="Export the Notes register (summary + line items) for the current filter"
            >
              Export
            </Button>
          }
          rows={pivotRows}
          rowKey={(r) => r.key}
          loading={query.isFetching && rows.length === 0}
          columns={[
            { key: 'store', header: 'Store', minWidth: '160px', cell: (r) => <strong>{r.storeName}</strong> },
            { key: 'period', header: 'Period', minWidth: '170px', cell: (r) => `${formatDate(r.periodStart)} – ${formatDate(r.periodEnd)}` },
            // Two columns per category: the amount, then the note number it was issued under.
            ...noteCategories.flatMap((c) => [
              {
                key: `cat_${c}`,
                header: c,
                align: 'right' as const,
                minWidth: '120px',
                cell: (r: PivotRow) => {
                  const n = r.byCat[c];
                  return n ? <strong className={n.amount >= 0 ? 'text-accent' : 'text-danger'}>₹{formatINR(n.amount)}</strong> : '—';
                },
              },
              {
                key: `cat_${c}_no`,
                header: `${c} Note No`,
                minWidth: '140px',
                cell: (r: PivotRow) => r.byCat[c]?.noteNo ?? '—',
              },
            ]),
            { key: 'by', header: 'By', cell: (r) => r.createdBy ?? '—' },
          ]}
        />
      )}

      <div className={rows.length > 0 ? 'mt-6' : ''}>
        <Table<SisFifoItemRow>
          storageKey="sis-fifo-by-item"
          paginated
          title={`FIFO Cost vs Sell — by Item${group ? ` (${group})` : ''}`}
          count={fifoTotals ? `${fifoRows.length} items · FIFO cost ₹${formatINR(fifoTotals.fifoCost)} · Sell ₹${formatINR(fifoTotals.sellValue)} · Diff ₹${formatINR(fifoTotals.diff)}` : `${fifoRows.length} items`}
          titleAction={
            <Button
              variant="glass"
              size="sm"
              icon={<Download size={12} />}
              disabled={fifoRows.length === 0}
              onClick={() => downloadExcel(sisFifoByItemExportPath(fifoFilter), 'GRHAMOY_SIS_FIFO_CostVsSell.xlsx', 120000)}
              title="Export the FIFO Cost-vs-Sell by-item table for the current filter"
            >
              Export
            </Button>
          }
          rows={fifoRows}
          rowKey={(r) => `${r.itemId}|${r.txnId}|${r.invoiceNo}`}
          loading={fifoQ.isFetching && fifoRows.length === 0}
          empty="No sale allocations for this filter — upload store sales reports (SIS Sales Report) to populate FIFO differences."
          columns={[
            { key: 'item', header: 'Item', minWidth: '200px', cell: (r) => <span className="font-bold">{r.itemName}</span> },
            { key: 'barcode', header: 'Barcode', minWidth: '110px', cell: (r) => r.barcode || '—' },
            { key: 'bill', header: 'Bill No', minWidth: '120px', cell: (r) => r.txnId },
            { key: 'srcinv', header: 'Src Invoice', minWidth: '120px', cell: (r) => r.invoiceNo },
            { key: 'qty', header: 'Qty Sold', align: 'right', cell: (r) => r.qty },
            { key: 'fifoRate', header: 'FIFO Rate/Pc', align: 'right', minWidth: '110px', cell: (r) => `₹${formatINR(r.avgFifoRate)}` },
            { key: 'sellRate', header: 'Sell Rate/Pc (Net)', align: 'right', minWidth: '120px', cell: (r) => `₹${formatINR(r.avgSellRate)}` },
            { key: 'fifoCost', header: 'FIFO Cost', align: 'right', minWidth: '120px', cell: (r) => `₹${formatINR(r.fifoCost)}` },
            { key: 'sell', header: 'Sell Value (Net)', align: 'right', minWidth: '120px', cell: (r) => `₹${formatINR(r.sellValue)}` },
            { key: 'diff', header: 'Difference (Cost − Sell)', align: 'right', minWidth: '140px', cell: (r) => (r.diff === 0 ? '—' : <strong className={r.diff > 0 ? 'text-danger' : 'text-success'}>₹{formatINR(r.diff)}</strong>) },
          ]}
        />
      </div>

      <GenerateCreditNoteModal open={genOpen} onClose={() => setGenOpen(false)} />
    </div>
  );
}
